<template>
  <Component
    ref="buttonEl"
    :is="componentType"
    :type="buttonType"
    :href="href"
    :target="target"
    :class="classes"
    :tabindex="tabindex"
    :aria-disabled="disabled || loading"
    :style="loadingStyles"
  >
    <div v-if="loading" aria-label="Loading"
         class="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center z-[1]">
      <div class="dot"/>
      <div class="dot"/>
      <div class="dot"/>
    </div>
    <div :class="contentClasses">
      <slot/>
    </div>
  </Component>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {twMerge} from "tailwind-merge";

const props = defineProps({
  color: {
    type: String,
    required: true,
    default: "secondary",
    validator: value => ["primary", "secondary", "outline", "outline-white", "outline-orange", "outline-red", "outline-green", "amber",
      "outline-blue", "gradient", "danger", "warning", "white", "green", "outline-primary", "red", "secondary-dark",
      "white-translucent", "outline-secondary", "hex", "transparent", "black", "transparent-slate", "match", "none",
      "blue", "connect", "hire"
    ].includes(value),
  },
  size: {
    type: String,
    default: "md",
    validator: value => ["xxs", "xs", "sm", "md", "lg", "xl", "lg-wide", "tall", "full", "icon"].includes(value),
  },
  type: {
    type: String,
    default: "button",
  },
  href: {
    type: String,
    default: undefined,
  },
  target: {
    type: String,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  useDiv: {
    type: Boolean,
    default: false,
  },
  useSpan: {
    type: Boolean,
    default: false,
  },
  useRawLink: {
    type: Boolean,
    default: false,
  },
  flex: {
    type: Boolean,
    default: false,
  },
  justifyStart: {
    type: Boolean,
    default: false,
  },
  bold: {
    type: Boolean,
    default: false,
  },
  medium: {
    type: Boolean,
    default: false,
  },
  hexForeground: {
    type: String,
    default: "",
  },
  hexBackground: {
    type: String,
    default: "",
  },
  overrideClasses: {
    type: String,
    default: "",
  },
  useRouterLink: {
    type: Boolean,
    default: false,
  },
  focusOnMount: {
    type: Boolean,
    default: false,
  },
});

const componentType = computed(() => {
  if (props.useDiv) return "div";
  if (props.useSpan) return "span";
  if (props.useRouterLink) return "router-link";
  if (props.useRawLink) return "a";
  return "button";
});
const buttonType = computed(() => (props.useDiv ? undefined : props.type || "button"));
const tabindex = computed(() => (props.disabled ? -1 : 0));
const buttonEl = ref(null);

const classes = computed(() => {
  const classes = {
    "relative rounded-lg transition-all block css-vars text-center": true,
    "text-white bg-primary-600 hover:bg-primary-700 hover:shadow-md active:shadow-lg": props.color === "primary",
    "text-white bg-green-600 hover:bg-green-700 hover:shadow-md active:shadow-lg": props.color === "green",
    "text-slate-600 bg-slate-200 hover:brightness-95 active:bg-slate-300": props.color === "secondary",
    "text-white bg-[#343a40] hover:bg-black": props.color === "secondary-dark",
    "text-white bg-bdBlue-600 hover:bg-bdBlue-700 focus:outline-none outline-none": props.color === "blue",
    "border border-slate-900 text-slate-900": props.color === "outline",
    "border border-white text-white opacity-90 hover:opacity-100 focus:outline-none": props.color === "outline-white",
    "bg-transparent text-slate-600 hover:text-slate-700": props.color === "transparent",
    "bg-white text-white bg-opacity-20": props.color === "white-translucent",
    "text-white bg-black opacity-90 hover:opacity-100 hover:shadow-lg": props.color === "black",
    "bg-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-700": props.color === "transparent-slate",
    "bg-hex text-hex hover:brightness-95": props.color === "hex",
    "border border-slate-200 bg-white hover:bg-slate-100 hover:border-slate-300 text-slate-600 hover:text-slate-700": props.color === "outline-secondary",
    "border border-bdOrange-600 text-bdOrange-600 hover:border-bdOrange-700 active:border-bdOrange-800 hover:text-bdOrange-700 active:text-bdOrange-600 focus:outline-none": props.color === "outline-orange",
    "border border-red-600 text-red-600 hover:border-red-700 active:border-red-800 hover:text-red-700 active:text-red-600 focus:outline-none": props.color === "outline-red",
    "border border-bdBlue-600 text-bdBlue-600 hover:border-bdBlue-700 active:border-bdBlue-800 hover:text-bdBlue-700 active:text-bdBlue-600 focus:outline-none": props.color === "outline-blue",
    "border border-green-600 text-green-600 hover:border-green-700 active:border-green-800 hover:text-green-700 active:text-green-600 focus:outline-none": props.color === "outline-green",
    "border border-primary-600 text-primary-600 hover:border-primary-700 active:border-primary-800 hover:text-primary-700 active:text-primary-600 focus:outline-none": props.color === "outline-primary",
    "text-white bg-gradient glint font-medium hover:shadow-lg hover:brightness-95 active:brightness-90 focus:outline-none focus:ring-2 focus:ring-gradient ring-offset-2": props.color === "gradient",
    "text-white bg-match glint font-medium hover:shadow-lg hover:brightness-95 active:shadow-md active:brightness-90": props.color === "match",
    "text-white bg-connect glint font-medium hover:shadow-lg hover:brightness-95 active:shadow-md active:brightness-90": props.color === "connect",
    "text-white bg-hire glint font-medium hover:shadow-lg hover:brightness-95 active:shadow-md active:brightness-90": props.color === "hire",
    "text-red-600 bg-red-100 font-medium hover:text-red-800 hover:shadow-sm active:shadow-md hover:bg-red-200": props.color === "danger",
    "text-white bg-red-600 font-medium hover:bg-red-700 hover:shadow-sm active:shadow-md": props.color === "red",
    "text-white bg-amber-600 font-medium hover:bg-amber-700 hover:shadow-sm active:shadow-md": props.color === "amber",
    "text-amber-600 bg-amber-100 font-medium hover:text-amber-800 hover:shadow-sm active:shadow-md hover:bg-amber-200": props.color === "warning",
    "text-slate-900 bg-slate-100 hover:brightness-95 active:bg-slate-200 focus:outline-none focus:ring ring-white/50": props.color === "white",
    "px-1.5 sm:px-3 py-1.5 text-sm": props.size === "xs",
    "px-1.5 py-1 text-sm": props.size === "xxs",
    "px-2 sm:px-4 py-1.5": props.size === "sm",
    "px-4 sm:px-6 py-2": props.size === "md",
    "px-6 sm:px-8 py-2": props.size === "lg",
    "px-8 sm:px-10 py-3 text-": props.size === "xl",
    "px-4 py-3": props.size === "tall",
    "px-12 py-2.5": props.size === "lg-wide",
    "w-full py-2": props.size === "full",
    "p-2.5": props.size === "icon",
    "opacity-70 cursor-not-allowed": props.disabled,
    "font-bold": props.bold,
    "font-medium": props.medium,
    "cursor-pointer": !props.disabled,
  };

  const classesString = Object.keys(classes).filter(key => classes[key]).join(" ");
  return twMerge(classesString, props.overrideClasses);
});

const loadingStyles = computed(() => {
  const colors = {
    "outline": "#94a3b8",
    "outline-white": "#fff",
    "outline-orange": "#fff",
    "outline-red": "#dc2626",
    "outline-blue": "#94a3b8",
    "outline-green": "#16a34a",
    "outline-primary": "#2c67f6",
    "gradient": "#fff",
    "danger": "#f87171",
    "secondary": "#475569",
    "warning": "#fbbf24",
    "white": "#94a3b8",
    "green": "#10b981",
    "red": "#ef4444",
    "black": "#fff",
    "match": "#fff",
    "connect": "#fff",
    "hire": "#fff",
    "transparent-slate": "#475569",
    "hex": props.hexForeground || "#475569",
  };
  return `--loading-color:${colors[props.color]};--hex-foreground:${props.hexForeground || "#475569"};--hex-background:${props.hexBackground || "#e2e8f0"}`;
});

const contentClasses = computed(() => ({
  "opacity-0": props.loading,
  "flex items-center gap-2": props.flex,
  "justify-start": props.justifyStart,
  "justify-center": !props.justifyStart,
}));

defineExpose({
  focus: () => buttonEl.value?.focus(),
});

onMounted(() => props.focusOnMount && buttonEl.value && buttonEl.value.focus());
</script>

<style scoped>
.css-vars {
  --loading-color: #fff;
  --hex-foreground: #475569;
  --hex-background: #e2e8f0;
}

.text-hex {
  color: var(--hex-foreground);
}

.bg-hex {
  background-color: var(--hex-background);
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin: 0 5px;
  animation: bounce 0.6s infinite;
}

@keyframes bounce {
  0%, 50% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-6px);
  }
}

.dot:nth-of-type(1) {
  background-color: var(--loading-color);
  animation-delay: 0s;
}

.dot:nth-of-type(2) {
  background-color: var(--loading-color);
  animation-delay: 0.08s;
}

.dot:nth-of-type(3) {
  background-color: var(--loading-color);
  animation-delay: 0.16s;
}
</style>
